<template>
    <div class="ai-floating" :class="'ai-floating--' + store.widgetPosition">
        <AssistantModal />
        <button
            class="ai-floating__btn"
            :class="{ active: store.isOpen }"
            @click="store.toggle"
            :title="store.isOpen ? 'Закрити асистента' : 'Відкрити AI асистента'"
        >
            <svg v-if="store.isOpen" class="ai-floating__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 6L18 18M6 18L18 6" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
            </svg>
            <span v-else-if="activeIcon.type === 'emoji'" class="ai-floating__emoji">{{ activeIcon.emoji }}</span>
            <svg v-else class="ai-floating__icon" :viewBox="activeIcon.viewBox" fill="none" xmlns="http://www.w3.org/2000/svg" v-html="activeIcon.path" />
        </button>
    </div>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { useAiAssistantStore } from '@/stores/aiAssistant'
import AssistantModal from './AssistantModal.vue'

const FAB_ICONS = {
    smiley: {
        type: 'svg', viewBox: '0 0 24 24',
        path: '<path d="M3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12V17C21 18.1046 20.1046 19 19 19H12C7.02944 19 3 14.9706 3 10V12Z" fill="white" fill-opacity="0.25"/><path d="M8 11.5C8 10.6716 8.67157 10 9.5 10C10.3284 10 11 10.6716 11 11.5C11 12.3284 10.3284 13 9.5 13C8.67157 13 8 12.3284 8 11.5Z" fill="white"/><path d="M13 11.5C13 10.6716 13.6716 10 14.5 10C15.3284 10 16 10.6716 16 11.5C16 12.3284 15.3284 13 14.5 13C13.6716 13 13 12.3284 13 11.5Z" fill="white"/><path d="M9 15.5C9.5 16.5 10.6 17 12 17C13.4 17 14.5 16.5 15 15.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>',
    },
    chat: {
        type: 'svg', viewBox: '0 0 24 24',
        path: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    },
    food: {
        type: 'svg', viewBox: '0 0 24 24',
        path: '<path d="M18 8h1a4 4 0 0 1 0 8h-1M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="6" y1="1" x2="6" y2="4" stroke="white" stroke-width="2" stroke-linecap="round"/><line x1="10" y1="1" x2="10" y2="4" stroke="white" stroke-width="2" stroke-linecap="round"/><line x1="14" y1="1" x2="14" y2="4" stroke="white" stroke-width="2" stroke-linecap="round"/>',
    },
    headset: {
        type: 'svg', viewBox: '0 0 24 24',
        path: '<path d="M3 18v-6a9 9 0 0 1 18 0v6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    },
    bot: {
        type: 'svg', viewBox: '0 0 24 24',
        path: '<rect x="3" y="11" width="18" height="10" rx="2" stroke="white" stroke-width="2"/><circle cx="12" cy="5" r="2" stroke="white" stroke-width="2"/><line x1="12" y1="7" x2="12" y2="11" stroke="white" stroke-width="2"/><circle cx="8" cy="16" r="1" fill="white"/><circle cx="16" cy="16" r="1" fill="white"/>',
    },
    'emoji-sparkles': { type: 'emoji', emoji: '✨' },
    'emoji-wave':     { type: 'emoji', emoji: '👋' },
    'emoji-robot':    { type: 'emoji', emoji: '🤖' },
    'emoji-chat':     { type: 'emoji', emoji: '💬' },
    'emoji-food':     { type: 'emoji', emoji: '🍽️' },
    'emoji-bulb':     { type: 'emoji', emoji: '💡' },
    'emoji-star':     { type: 'emoji', emoji: '⭐' },
    'emoji-fire':     { type: 'emoji', emoji: '🔥' },
}

const store = useAiAssistantStore()

const activeIcon = computed(() =>
    FAB_ICONS[store.widgetIcon] || FAB_ICONS.smiley
)

onMounted(() => store.fetchWidgetConfig())
</script>

<style scoped lang="scss">
.ai-floating {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 3050;

    &--left {
        right: auto;
        left: 20px;
    }

    &__btn {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: linear-gradient(135deg, var(--widget-primary, #f0a500), color-mix(in srgb, var(--widget-primary, #f0a500) 70%, #000));
        border: none;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transform-origin: center center;
        transition: transform 0.2s ease, box-shadow 0.3s ease;

        /* «Живий» пульс, коли чат закритий — як welcome-orb у модалці */
        &:not(.active) {
            animation: fab-breathe 2.8s ease-in-out infinite;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        }

        &:not(.active):hover {
            animation-play-state: paused;
        }

        &:hover {
            transform: scale(1.1);
            box-shadow:
                0 4px 20px rgba(0, 0, 0, 0.2),
                0 0 0 6px color-mix(in srgb, var(--widget-primary, #f0a500) 25%, transparent);
        }

        &.active {
            animation: none;
            background: linear-gradient(135deg, #666, #444);

            &:hover {
                box-shadow:
                    0 4px 20px rgba(0, 0, 0, 0.25),
                    0 0 0 6px rgba(100, 100, 100, 0.2);
            }
        }
    }

    &__icon {
        width: 26px;
        height: 26px;
        transform-origin: center center;

        .ai-floating__btn:not(.active) & {
            animation: fab-inner-pulse 2.8s ease-in-out infinite;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        }

        .ai-floating__btn:not(.active):hover & {
            animation-play-state: paused;
        }
    }

    &__emoji {
        font-size: 26px;
        line-height: 1;
        display: block;
        transform-origin: center center;

        .ai-floating__btn:not(.active) & {
            animation: fab-emoji-glow 2.8s ease-in-out infinite;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        }

        .ai-floating__btn:not(.active):hover & {
            animation-play-state: paused;
        }
    }
}

@keyframes fab-breathe {
    0%, 100% {
        transform: scale(1);
        box-shadow:
            0 4px 16px rgba(0, 0, 0, 0.2),
            0 0 0 0 color-mix(in srgb, var(--widget-primary, #f0a500) 35%, transparent);
    }
    50% {
        transform: scale(1.07);
        box-shadow:
            0 8px 24px rgba(0, 0, 0, 0.22),
            0 0 0 8px color-mix(in srgb, var(--widget-primary, #f0a500) 0%, transparent);
    }
}

@keyframes fab-inner-pulse {
    0%, 100% { filter: brightness(1); opacity: 0.96; }
    50% { filter: brightness(1.12); opacity: 1; }
}

@keyframes fab-emoji-glow {
    0%, 100% { filter: brightness(1); transform: scale(1); }
    50% { filter: brightness(1.1); transform: scale(1.05); }
}
</style>
